// =======================================================
// NoiseFlow — ambient flow-field, cheap fullscreen shader
// =======================================================
//
// We evaluate FBM at each pixel and map its iso-lines to brightness.
// Animation comes from looped domain warping so different regions drift at
// different rates instead of the whole texture sliding in one direction.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uLoopPhase;
  uniform vec2  uResolution;
  uniform vec3  uColor;
  uniform vec3  uBg;
  uniform float uScale;
  uniform float uSpeed;
  uniform float uRandomness;
  uniform float uContrast;
  uniform float uBands;   // number of iso-bands (0 = continuous)
  uniform float uAspect;

  ${GLSL_COMMON}

  void main() {
    // keep scale uniform regardless of aspect (multiply x by aspect)
    vec2 p = vec2(vUv.x * uAspect, vUv.y) * uScale;
    float ang = uLoopPhase * 6.2831;
    float drift = uSpeed * 0.35;

    vec2 orbitA = vec2(cos(ang), sin(ang)) * drift;
    vec2 orbitB = vec2(cos(ang * 1.7 + 2.1), sin(ang * 1.3 - 0.8)) * drift;
    vec2 orbitC = vec2(cos(ang * 2.3 - 1.4), sin(ang * 1.9 + 0.6)) * drift;

    vec2 q = vec2(
      fbm(p * 0.85 + orbitA + vec2(4.7, 1.3)),
      fbm(p * 0.85 - orbitB + vec2(8.1, 6.2))
    ) * 2.0 - 1.0;
    vec2 r = vec2(
      fbm(p * 1.65 + q * 1.4 + orbitC + vec2(2.4, 9.6)),
      fbm(p * 1.65 - q * 1.2 - orbitA + vec2(7.2, 3.5))
    ) * 2.0 - 1.0;

    vec2 localDrift = (q * 1.25 + r * 0.75) * uRandomness;
    vec2 residualShift = orbitA * (1.0 - uRandomness) * 0.7;
    float f = fbm(p + localDrift + residualShift);

    // pseudo-iso lines: repeat brightness around uBands rings
    float v;
    if (uBands > 0.0) {
      v = abs(fract(f * uBands) - 0.5) * 2.0;
      v = pow(1.0 - v, uContrast * 4.0);
    } else {
      v = pow(f, uContrast);
    }

    vec3 col = mix(uBg, uColor, clamp(v, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class NoiseFlow extends Generator {
  constructor() {
    super({
      name: 'Noise Flow',
      category: 'ambient',
      schema: {
        scale:    { default: 3.0, min: 0.5,  max: 12,  step: 0.1,  label: 'Scale' },
        speed:    { default: 0.4, min: 0,    max: 4,   step: 0.01, label: 'Speed' },
        randomness:{ default: 0.8, min: 0,    max: 2,   step: 0.01, label: 'Randomness' },
        contrast: { default: 1.2, min: 0.2,  max: 4,   step: 0.05, label: 'Contrast' },
        bands:    { default: 4,   min: 0,    max: 20,  step: 0.5,  label: 'Iso-bands (0=smooth)' },
        accent:   { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uLoopPhase:  { value: 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColor:      { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color('#000000') },
        uScale:      { value: this.v('scale') },
        uSpeed:      { value: this.v('speed') },
        uRandomness: { value: this.v('randomness') },
        uContrast:   { value: this.v('contrast') },
        uBands:      { value: this.v('bands') },
        uAspect:     { value: engine.width / engine.height },
      },
      transparent: false, depthWrite: false, depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    u.uResolution.value.set(w, h);
    u.uAspect.value = w / h;
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent') u.uColor.value.set(val);
    else if (`u${key[0].toUpperCase()}${key.slice(1)}` in u) {
      u[`u${key[0].toUpperCase()}${key.slice(1)}`].value = val;
    }
  }

  update(dt, time, engine) {
    if (this.mesh) this.mesh.material.uniforms.uLoopPhase.value = engine.getLoopInfo(time).phase;
  }
}
